"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useNewLabRequisition = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const react_1 = require("react");
const context_1 = require("../context");
const utils_1 = require("../utils");
const useDebouncedAbortableCall_1 = require("./useDebouncedAbortableCall");
const templates_1 = require("@home-health-notify/templates");
const react_2 = __importDefault(require("react"));
const chat_1 = __importDefault(require("../i18n/en/chat"));
const useNewLabRequisition = (onSubmit, groupInfo, googleMapsAPIKey, fetchAddressFromCoordinates, labRequisitionId) => {
    const { selectedOrgUser } = (0, react_1.useContext)(context_1.OrganizationContext);
    const [isFormSubmitting, setIsFormSubmitting] = (0, react_1.useState)(false);
    const [template, setTemplate] = (0, react_1.useState)();
    const [formData, setFormData] = (0, react_1.useState)();
    const [hasNoLabs, setHasNoLabs] = (0, react_1.useState)(false);
    const [errors, setErrors] = (0, react_1.useState)([]);
    const [validationMode, setValidationMode] = (0, react_1.useState)(templates_1.ValidationModeJFC.ValidateAndHide);
    const [additionalErrors, setAdditionalErrors] = (0, react_1.useState)([]);
    const { labRequisitions } = (0, react_1.useContext)(context_1.LabRequisitionsContext);
    const [labRequisitionData, setLabRequisitionData] = react_2.default.useState();
    const fetchLabRequisitions = (0, useDebouncedAbortableCall_1.useDebouncedAbortableCall)((abortContext) => __awaiter(void 0, void 0, void 0, function* () {
        if (!!selectedOrgUser && !!chat_1.default) {
            yield selectedOrgUser.labsManager.getLabRequisitions(groupInfo.id, abortContext);
        }
    }), utils_1.Constants.API_DEBOUNCE_DELAY_IN_MS);
    (0, react_1.useEffect)(() => {
        if (labRequisitionId) {
            fetchLabRequisitions();
        }
    }, [labRequisitionId]);
    (0, react_1.useEffect)(() => {
        if (labRequisitionId && labRequisitions) {
            const labRequisitionData = labRequisitions.find(r => r.id === labRequisitionId);
            setLabRequisitionData(labRequisitionData);
        }
    }, [labRequisitions]);
    const fetchPatientDetails = (0, useDebouncedAbortableCall_1.useDebouncedAbortableCall)((abortContext, mrn) => __awaiter(void 0, void 0, void 0, function* () {
        yield selectedOrgUser.emrManager.fetchPatientInformation(mrn, abortContext);
    }), utils_1.Constants.API_DEBOUNCE_DELAY_IN_MS);
    const fetchLabs = (0, useDebouncedAbortableCall_1.useDebouncedAbortableCall)((abortContext) => __awaiter(void 0, void 0, void 0, function* () {
        yield selectedOrgUser.labsManager.getLabs(abortContext);
    }), utils_1.Constants.API_DEBOUNCE_DELAY_IN_MS);
    const fetchLabDetails = (0, useDebouncedAbortableCall_1.useDebouncedAbortableCall)((abortContext, labId) => __awaiter(void 0, void 0, void 0, function* () {
        yield selectedOrgUser.labsManager.getLabDetails(labId, abortContext);
    }), utils_1.Constants.API_DEBOUNCE_DELAY_IN_MS);
    const handleFormSubmit = () => __awaiter(void 0, void 0, void 0, function* () {
        if ((errors === null || errors === void 0 ? void 0 : errors.length) > 0 || (additionalErrors === null || additionalErrors === void 0 ? void 0 : additionalErrors.length) > 0) {
            setValidationMode(templates_1.ValidationModeJFC.ValidateAndShow);
            return false;
        }
        else {
            setIsFormSubmitting(true);
            try {
                const formMappedData = formData && (template === null || template === void 0 ? void 0 : template.formToPayloadDataHandler(formData, template));
                (formMappedData === null || formMappedData === void 0 ? void 0 : formMappedData.form) &&
                    (yield selectedOrgUser.labsManager.requestLab(formMappedData.form));
            }
            finally {
                setIsFormSubmitting(false);
            }
            onSubmit();
            return true;
        }
    });
    const addAdditionalErrors = (error) => {
        if (!additionalErrors.find(e => e.instancePath === error.instancePath && e.keyword === error.keyword)) {
            setAdditionalErrors([...additionalErrors, error]);
        }
    };
    const clearAdditionalErrors = (error) => {
        setAdditionalErrors(additionalErrors.filter(e => !(e.instancePath === error.instancePath && e.keyword === error.keyword)));
    };
    const extractLastNameAndMRN = (patientLastName) => {
        const match = patientLastName.match(/^(.+?)(?:\s*-\s*(\d+))?\s*$/);
        if (match) {
            if (match.length === 2) {
                return { lastName: match[1], mrn: undefined };
            }
            else if (match.length === 3) {
                return { lastName: match[1], mrn: match[2] };
            }
        }
        return { lastName: patientLastName, mrn: undefined };
    };
    const getPatientLastNameAndMRN = () => {
        var _a;
        const lastName = (_a = groupInfo.patientInfo) === null || _a === void 0 ? void 0 : _a.lastName;
        if (!!lastName) {
            return extractLastNameAndMRN(lastName);
        }
        return { lastName, mrn: undefined };
    };
    const fetchICDCodes = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d;
        const primaryDiagnosis = (_b = (_a = selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.emrManager.patientInformation) === null || _a === void 0 ? void 0 : _a.diagnosis) === null || _b === void 0 ? void 0 : _b.primaryDiagnosis;
        const secondaryDiagnosis = (_d = (_c = selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.emrManager.patientInformation) === null || _c === void 0 ? void 0 : _c.diagnosis) === null || _d === void 0 ? void 0 : _d.secondaryDiagnosis;
        return [primaryDiagnosis, secondaryDiagnosis]
            .filter(icd10 => !!icd10)
            .map(icd10 => {
            return {
                icdCode: JSON.stringify({
                    label: icd10.longDescription,
                    value: icd10.code,
                }),
            };
        });
    });
    (0, react_1.useEffect)(() => {
        const initLabsTemplate = () => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            const address = (_a = groupInfo.patientInfo) === null || _a === void 0 ? void 0 : _a.address;
            const { mrn } = getPatientLastNameAndMRN();
            const apiCalls = [fetchLabs()];
            apiCalls.push(fetchPatientDetails(mrn));
            apiCalls.push(!!(address === null || address === void 0 ? void 0 : address.lat) && !!address.lng
                ? fetchAddressFromCoordinates(address.lat, address.lng, googleMapsAPIKey)
                : Promise.resolve());
            const [, , parsedAddress] = yield Promise.all(apiCalls);
            const labsData = selectedOrgUser.labsManager.labsData;
            if (!labsData || labsData.length === 0) {
                setHasNoLabs(true);
                return;
            }
            const diagnosisICD10s = yield fetchICDCodes();
            const labsTemplate = (0, templates_1.labRequisitionTemplate)(labsData);
            const withGroupId = (labRequisitionData, groupId) => (Object.assign(Object.assign({}, labRequisitionData), {
                patientInformation: Object.assign(Object.assign({}, labRequisitionData.patientInformation), { id: groupId }),
            }));
            const preloadInitialData = (labsTemplate, groupInfo) => {
                var _a, _b, _c, _d, _e, _f, _g, _h;
                const firstName = (_a = groupInfo.patientInfo) === null || _a === void 0 ? void 0 : _a.firstName;
                const dateOfBirth = ((_b = groupInfo.patientInfo) === null || _b === void 0 ? void 0 : _b.dob) &&
                    (0, dayjs_1.default)(groupInfo.patientInfo.dob, "MM/DD/YYYY").valueOf();
                const address = (_c = groupInfo.patientInfo) === null || _c === void 0 ? void 0 : _c.address;
                let city, state, country, zipCode, streetNumber, route, addressOne, addressTwo;
                if (address && parsedAddress) {
                    ({ streetNumber, route, city, state, country, zipCode } =
                        parsedAddress);
                    (streetNumber || route) &&
                        (addressOne = [streetNumber, route].filter(i => !!i).join(" "));
                    address.apartmentNo && (addressTwo = address.apartmentNo);
                }
                const phoneNumber = (_d = groupInfo.patientInfo) === null || _d === void 0 ? void 0 : _d.phoneNo;
                const { lastName } = getPatientLastNameAndMRN();
                const gender = (_e = selectedOrgUser.emrManager.patientInformation) === null || _e === void 0 ? void 0 : _e.gender;
                return Object.assign(Object.assign({}, labsTemplate), { initialData: Object.assign(Object.assign({}, labsTemplate.initialData), { patientInformation: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, labsTemplate.initialData.patientInformation), { id: groupInfo.id }), (firstName && { firstName })), (lastName && { lastName })), (gender && { gender })), (dateOfBirth && { dateOfBirth })), (addressOne && { addressOne })), (addressTwo && { addressTwo })), (phoneNumber && { phoneNumber })), (city && { city })), (country && { country })), (state && { state })), (zipCode && { zipCode })), providerInformation: {
                            providerNPI: (_g = (_f = selectedOrgUser.emrManager.patientInformation) === null || _f === void 0 ? void 0 : _f.physician) === null || _g === void 0 ? void 0 : _g.npi,
                        }, diagnosis: diagnosisICD10s, insuranceInformation: Object.assign(Object.assign({}, labsTemplate.initialData.insuranceInformation), (_h = selectedOrgUser.emrManager.patientInformation) === null || _h === void 0 ? void 0 : _h.insurance) }) });
            };
            const withInitialData = (labRequisitionData) => (Object.assign(Object.assign({}, labsTemplate), {
                initialData: labsTemplate.payloadToFormDataHandler({
                    form: labRequisitionData,
                }, labsTemplate),
            }));
            const templateData = labRequisitionData
                ? withInitialData(withGroupId(labRequisitionData, groupInfo.id))
                : preloadInitialData(labsTemplate, groupInfo);
            if (!!((_b = templateData.initialData.labInformation) === null || _b === void 0 ? void 0 : _b.labId)) {
                yield fetchLabDetails(templateData.initialData.labInformation.labId);
            }
            setFormData(templateData.initialData);
            setTemplate(templateData);
        });
        (!labRequisitionId || labRequisitionData) && initLabsTemplate();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [labRequisitionData]);
    const currentlyLoadedCompendiumLabId = (0, react_1.useRef)(undefined);
    const handleChange = (_a) => __awaiter(void 0, [_a], void 0, function* ({ data, errors, }) {
        var _b, _c, _d;
        if (!!((_b = data.labInformation) === null || _b === void 0 ? void 0 : _b.labId) &&
            data.labInformation.labId !==
                ((_c = selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.labsManager.labDetails) === null || _c === void 0 ? void 0 : _c.id) &&
            !!template) {
            yield fetchLabDetails(data.labInformation.labId);
        }
        if ((selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.labsManager.labDetails) &&
            selectedOrgUser.labsManager.labDetails.id !==
                currentlyLoadedCompendiumLabId.current &&
            !!template) {
            setTemplate(Object.assign(Object.assign({}, template), { formToPayloadDataHandler: (0, templates_1.scheduleOrderLabFormToPayloadDataHandler)(selectedOrgUser.labsManager.labsData, (_d = selectedOrgUser === null || selectedOrgUser === void 0 ? void 0 : selectedOrgUser.labsManager.labDetails) === null || _d === void 0 ? void 0 : _d.compendium) }));
            currentlyLoadedCompendiumLabId.current =
                selectedOrgUser.labsManager.labDetails.id;
        }
        setFormData(data);
        setErrors(errors);
    });
    return {
        template,
        formData,
        handleChange,
        validationMode,
        additionalErrors,
        addAdditionalErrors,
        clearAdditionalErrors,
        isFormSubmitting,
        handleFormSubmit,
        hasNoLabs,
    };
};
exports.useNewLabRequisition = useNewLabRequisition;
